import type { FC } from 'react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { useIsOldPointshopItemsDisabled } from '../../../../common/hooks/bonus2026FeatureFlagHooks';
import { SearchResultAbsoluteLink } from '../../../../search/components/GlobalSearchResultPage/SearchResultLink';
import { useGlobalSearchResultState } from '../../../../search/hooks/useGlobalSearchState';
import { useProductSearchStateContext } from '../../../../search/hooks/useProductSearchStateContext';
import {
    getNonEmptyResultTypes,
    getSearchResultCountName,
} from '../../../../search/utils/searchResultUtils';
import SearchOverlayActions from '../SearchOverlay/SearchOverlayActions';
import styles from './EcommerceSearchOverlay.module.scss';

interface NoEcommerceSearchOverlayResultsProps {
    isLoading: boolean;
    query?: string;
    queryUsed?: string;
    onHintClick?: (hint: string) => void;
}

const NoEcommerceSearchOverlayResults: FC<
    React.PropsWithChildren<NoEcommerceSearchOverlayResultsProps>
> = (props) => {
    const isOldPointshopItemsDisabled = useIsOldPointshopItemsDisabled();
    const {
        result: globalResult,
        fetch,
        isLoading: isGlobalLoading,
    } = useGlobalSearchResultState({
        pageCounts: {
            articles: 1,
            products: 1,
            stores: 1,
            recipes: 1,
            pointshop: 1,
        },
        quickSearch: true,
        excludePointShopItems: isOldPointshopItemsDisabled,
    });

    const { setA11yResultAnnounceMessage } = useProductSearchStateContext();

    const [cancelled, setCancelled] = useState(false);

    useEffect(() => {
        // avoid race condition between product result and global result rerenders
        setCancelled(props.isLoading);
    }, [props.isLoading]);

    useEffect(() => {
        if (props.queryUsed && !cancelled) {
            fetch({ query: props.queryUsed });
        }
    }, [fetch, cancelled, props.queryUsed]);

    const nonEmptyResults = getNonEmptyResultTypes(globalResult);

    const nonEmptyResultLinks =
        nonEmptyResults?.length &&
        nonEmptyResults.map((item, index) => (
            <React.Fragment key={item.resultType}>
                <SearchResultAbsoluteLink
                    resultType={item.resultType}
                    searchTerm={props.query}
                    additionalClasses="u-paddingAxxxxsm"
                >
                    {getSearchResultCountName(item)}
                </SearchResultAbsoluteLink>
                {index < nonEmptyResults.length - 1 &&
                    (index < nonEmptyResults.length - 2 ? ', ' : ' och ')}
            </React.Fragment>
        ));

    const noResultsAtAll = !nonEmptyResults?.length;

    useEffect(() => {
        if (!isGlobalLoading && !cancelled && globalResult) {
            if (!nonEmptyResults?.length) {
                setA11yResultAnnounceMessage(
                    `Vi hittar inga resultat som innehåller ${globalResult?.queryUsed}`,
                );
            } else {
                setA11yResultAnnounceMessage(
                    `Inga varor hittades men det finns ${nonEmptyResults
                        .map((item) => getSearchResultCountName(item))
                        .join(', ')} för ${globalResult?.queryUsed}`,
                );
            }
        }
    }, [cancelled, globalResult, isGlobalLoading, nonEmptyResults, setA11yResultAnnounceMessage]);

    return (
        <>
            <div className={styles.EcommerceSearchOverlayNoResultsHeader}>
                {props.queryUsed && (
                    <p className={styles.EcommerceSearchOverlayResultsSummary}>
                        {noResultsAtAll ? (
                            <>Vi hittar inga resultat som innehåller ”{props.queryUsed}”</>
                        ) : (
                            <>
                                Inga varor hittades men det finns {nonEmptyResultLinks} för ”
                                {props.queryUsed}”
                            </>
                        )}
                    </p>
                )}
                <SearchOverlayActions onHintClick={props.onHintClick}>
                    {() => null}
                </SearchOverlayActions>
            </div>
            <div className="u-textCenter u-paddingA">
                <img src="/Assets/Icons/Varukorg.svg" alt="Varukorg" height="150px" />
            </div>
        </>
    );
};

export default NoEcommerceSearchOverlayResults;
